import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="transaction-selector"
export default class extends Controller {
  static targets = [
    "existingTransactionSelector",
    "newTransactionForm",
    "transactionSelect",
    "toggleContainer",
    "existingTransactionToggle",
    "newTransactionToggle",
    "existingTransactionText",
    "existingTransactionDetails",
    "existingDetailPaymentMethod",
    "existingDetailCardLast4",
    "existingDetailPaymentProcessor",
    "existingDetailAmount",
    "existingDetailOccurredOn"
  ]
  static values = { hasTransactions: Boolean, initialMode: String }

  connect() {
    this.setupManualFields()
    // Set initial state based on preserved transaction mode or defaults
    if (this.hasTransactionsValue) {
      // Check if we have a preserved mode from form submission
      if (this.initialModeValue === 'new') {
        this.showNewTransactionForm()
      } else {
        this.showExistingTransactionSelector()
      }
      this.syncExistingDetailsWithSelection()
    } else {
      // No existing transactions - just show the new transaction form
      // No toggle buttons to manage in this case
      this.newTransactionFormTarget.classList.remove("hidden")
      this.enableManualFields()
    }
  }

  showNewTransactionForm() {
    // Hide existing transaction selector (only if it exists)
    if (this.hasExistingTransactionSelectorTarget) {
      this.existingTransactionSelectorTarget.classList.add("hidden")
    }

    // Show new transaction form
    this.newTransactionFormTarget.classList.remove("hidden")

    // Check the new transaction radio button (only if it exists)
    if (this.hasNewTransactionToggleTarget) {
      this.newTransactionToggleTarget.checked = true
    }

    // Update hidden field to preserve this selection
    this.updateTransactionModeField('new')
    this.enableManualFields()

    // Clear any selected transaction
    if (this.hasTransactionSelectTarget) {
      this.transactionSelectTarget.value = ""
    }
    this.toggleExistingDetails(false)

    // Focus on first input in the new transaction form
    const firstInput = this.newTransactionFormTarget.querySelector("select, input")
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100)
    }
  }

  showExistingTransactionSelector() {
    // Only proceed if user has existing transactions
    if (!this.hasTransactionsValue) return

    // Show existing transaction selector
    if (this.hasExistingTransactionSelectorTarget) {
      this.existingTransactionSelectorTarget.classList.remove("hidden")
    }

    // Hide new transaction form
    this.newTransactionFormTarget.classList.add("hidden")

    // Check the existing transaction radio button (only if it exists)
    if (this.hasExistingTransactionToggleTarget) {
      this.existingTransactionToggleTarget.checked = true
    }

    // Update hidden field to preserve this selection
    this.updateTransactionModeField('existing')
    this.disableManualFields()

    // Focus on transaction select if it exists and user has transactions
    if (this.hasTransactionSelectTarget && this.hasTransactionsValue) {
      setTimeout(() => this.transactionSelectTarget.focus(), 100)
    }
  }

  handleTransactionChange(event) {
    const selectedOption = event.target.options[event.target.selectedIndex]
    const selectedValue = event.target.value

    if (selectedValue !== "") {
      this.showExistingTransactionSelector()
      this.populateExistingDetails(selectedOption)
      this.toggleExistingDetails(true)
    } else {
      this.toggleExistingDetails(false)
    }
  }

  populateExistingDetails(option) {
    if (!option) return
    this.setExistingDetailText("existingDetailPaymentMethodTarget",    option.dataset.paymentMethod)
    this.setExistingDetailText("existingDetailCardLast4Target",        option.dataset.cardLast4)
    this.setExistingDetailText("existingDetailPaymentProcessorTarget", option.dataset.paymentProcessor)
    this.setExistingDetailText("existingDetailAmountTarget",           option.dataset.amount)
    this.setExistingDetailText("existingDetailOccurredOnTarget",       option.dataset.occurredOn)
  }

  setExistingDetailText(targetName, value) {
    const hasTargetName = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}`
    if (!this[hasTargetName]) return
    this[targetName].textContent = value || "—"
  }

  toggleExistingDetails(show) {
    if (!this.hasExistingTransactionDetailsTarget) return
    this.existingTransactionDetailsTarget.classList.toggle("hidden", !show)
  }

  syncExistingDetailsWithSelection() {
    if (!this.hasTransactionSelectTarget) return
    const select = this.transactionSelectTarget
    if (select.value !== "") {
      this.populateExistingDetails(select.options[select.selectedIndex])
      this.toggleExistingDetails(true)
    } else {
      this.toggleExistingDetails(false)
    }
  }

  updateTransactionModeField(mode) {
    // Find the hidden transaction_mode field and update its value
    const transactionModeField = document.querySelector('input[name="transaction_mode"]')
    if (transactionModeField) {
      transactionModeField.value = mode
    }
  }

  setupManualFields() {
    if (this.hasNewTransactionFormTarget) {
      this.manualFields = Array.from(this.newTransactionFormTarget.querySelectorAll('input, select, textarea'))
      this.manualFields = this.manualFields.filter(el => el.getAttribute('name') !== 'transaction_mode')

      if (this.initialModeValue === 'existing' && this.hasTransactionsValue) {
        this.disableManualFields()
      } else {
        this.enableManualFields()
      }
    }
  }

  disableManualFields() {
    if (!this.manualFields) return
    this.manualFields.forEach(el => { el.disabled = true })
  }

  enableManualFields() {
    if (!this.manualFields) return
    this.manualFields.forEach(el => { el.disabled = false })
  }
}
